import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {GetFlexibleBenefitsRequest} from '../requests/GetFlexibleBenefitsRequest';
import {GetFixedBenefitsRequest} from '../requests/GetFixedBenefitsRequest';
import {GetFixedBenefitsResponse} from '../responses/GetFixedBenefitsResponse';
import {GetFlexibleBenefitsResponse} from '../responses/GetFlexibleBenefitsResponse';
import {GetFlexibleBenefitResponse} from '../responses/GetFlexibleBenefitResponse';
import {GetFixedBenefitResponse} from '../responses/GetFixedBenefitResponse';
import {EndpointService} from './endpoint.service';
import {GetPhotosRequest} from '../requests/GetPhotosRequest';
import {GetPhotosResponse} from '../responses/GetPhotosResponse';
import {Observable, of, shareReplay, tap, map, finalize} from 'rxjs';
import {GetProvidersCardInfoRequest} from "../requests/GetProvidersCardInfoRequest";
import {GetProvidersCardInfoResponse} from "../responses/GetProvidersCardInfoResponse";
import {ProviderCard} from "../models/provider-card";
import {GetOfferBenefitsResponse} from '../responses/GetOfferBenefitsResponse';

@Injectable({providedIn: 'root'})
export class BenefitsService {

  private providersCardInfoCache: any = {};
  private offerBenefitsCache: any = {};
  private flexibleBenefitsCache: any = {};
  private fixedBenefitsCache: any = {};
  // Tracks in-flight requests per cache key so concurrent calls (e.g. rapid scroll events)
  // reuse the same HTTP observable instead of firing duplicate requests that would
  // each append their response to the respective cache, duplicating cached items.
  private fixedBenefitsPendingRequests: { [companyId: number]: Observable<GetFixedBenefitsResponse> } = {};
  private flexibleBenefitsPendingRequests: { [key: string]: Observable<GetFlexibleBenefitsResponse> } = {};
  private providersCardInfoPendingRequest: Observable<{ totalResults: number, data: ProviderCard[] }> = null;
  private offerBenefitsPendingRequests: { [companyId: number]: Observable<GetOfferBenefitsResponse> } = {};
  constructor(private _http: HttpClient) {
  }

  getUserFlexibleBenefits(data: GetFlexibleBenefitsRequest): Observable<GetFlexibleBenefitsResponse> {
    if (this.flexibleBenefitsCache &&
    this.flexibleBenefitsCache[data.companyId] &&
    this.flexibleBenefitsCache[data.companyId][data.providerId]) {
      const cache = this.flexibleBenefitsCache[data.companyId][data.providerId];
      if (cache.cachedFilter === this.getKeyFromFilters(data)) {
        if (data.paginationCriteria.start === 0)
          return of(cache);
        else if (data.paginationCriteria.start + data.paginationCriteria.limit - 1 < cache.responseObject.data.length) {
          return of({
            ...cache,
            responseObject: {
              ...cache.responseObject,
              data: cache.responseObject.data.slice(data.paginationCriteria.start, data.paginationCriteria.start + data.paginationCriteria.limit)
            }
          });
        }
      }
    }

    // If a request for this companyId/providerId is already in flight, reuse it instead of
    // firing a duplicate HTTP call, which would otherwise append the same data twice.
    const pendingKey = `${data.companyId}_${data.providerId}`;
    if (this.flexibleBenefitsPendingRequests[pendingKey]) {
      return this.flexibleBenefitsPendingRequests[pendingKey];
    }

    const request = this._http.post<GetFlexibleBenefitsResponse>(EndpointService.getUserFlexibleBenefits, data)
      .pipe(tap({
        next: value => {
          const cache = this.flexibleBenefitsCache[data.companyId]?.[data.providerId] ?? {};
          if (!this.flexibleBenefitsCache[data.companyId]) {
            this.flexibleBenefitsCache[data.companyId] = {};
          }
          this.flexibleBenefitsCache[data.companyId][data.providerId] = {
            ...cache,
            cachedFilter: this.getKeyFromFilters(data),
            responseObject: {
              ...cache.responseObject,
              ...value.responseObject,
              data: [...cache.responseObject?.data ?? [], ...value.responseObject.data]
            }
          };
        },
        error: () => {}
      }),
      finalize(() => {
        delete this.flexibleBenefitsPendingRequests[pendingKey];
      }),
      shareReplay());

    this.flexibleBenefitsPendingRequests[pendingKey] = request;

    return request;
  }

  getUserProvidersCardInfo(data: GetProvidersCardInfoRequest): Observable<{ totalResults: number, data: ProviderCard[]}> {
    if (this.providersCardInfoCache &&
      this.providersCardInfoCache.data &&
      this.providersCardInfoCache.data.length > 0 &&
      this.providersCardInfoCache.cachedFilter !== undefined &&
      this.providersCardInfoCache.cachedFilter === this.getKeyFromFilters(data)) {
      // If the user goes back to the Flexible Benefits page,
      // we should return all cached data (when the filters remain the same)
      // to avoid user having to press the 'Load more' button again to see all previously rendered benefits
      if (data.paginationCriteria.start === 0)
        return of({
          totalResults: this.providersCardInfoCache.totalResults,
          data: this.providersCardInfoCache.data
        });
      else if (data.paginationCriteria.start + data.paginationCriteria.limit - 1 < this.providersCardInfoCache.data.length) {
        return of({
          totalResults: this.providersCardInfoCache.totalResults,
          data: this.providersCardInfoCache.data
            .slice(data.paginationCriteria.start, data.paginationCriteria.start + data.paginationCriteria.limit)
        });
      }
    }

    // If a request is already in flight, reuse it instead of firing a duplicate HTTP call,
    // which would otherwise append the same data twice (cache here is a single global object).
    if (this.providersCardInfoPendingRequest) {
      return this.providersCardInfoPendingRequest;
    }

    const request = this._http.post<GetProvidersCardInfoResponse>(EndpointService.getUserProvidersCardInfo, data)
      .pipe(tap({
        next: value => {
          let existingData = this.providersCardInfoCache?.data ?? [];
          if (this.providersCardInfoCache.cachedFilter !== this.getKeyFromFilters(data))
            existingData = [];
          this.providersCardInfoCache = {
            totalResults: value.responseObject.totalResults,
            data: [...existingData, ...value.responseObject.data],
            cachedFilter: this.getKeyFromFilters(data)
          };
        },
        error: () => {
        }
      }),
      map(value => ({
        totalResults: value.responseObject.totalResults,
        data: value.responseObject.data
      })),
      finalize(() => {
        this.providersCardInfoPendingRequest = null;
      }),
      shareReplay());

    this.providersCardInfoPendingRequest = request;

    return request;
  }

  getOfferBenefits(companyId: number): Observable<GetOfferBenefitsResponse> {
    if (this.offerBenefitsCache && this.offerBenefitsCache[companyId]) {
      return of(this.offerBenefitsCache[companyId]);
    }

    // If a request for this companyId is already in flight, reuse it instead of
    // firing a duplicate HTTP call.
    if (this.offerBenefitsPendingRequests[companyId]) {
      return this.offerBenefitsPendingRequests[companyId];
    }

    const url = EndpointService.getOfferBenefits + `?companyId=${companyId}`;
    const request = this._http.get<GetOfferBenefitsResponse>(url)
      .pipe(tap({
        next: value => {
          this.offerBenefitsCache[companyId] = value;
        },
        error: () => {
        }
      }),
      finalize(() => {
        delete this.offerBenefitsPendingRequests[companyId];
      }),
      shareReplay());

    this.offerBenefitsPendingRequests[companyId] = request;

    return request;
  }

  private getKeyFromFilters(data: GetFlexibleBenefitsRequest | GetProvidersCardInfoRequest, filterNameParams: string[] = undefined): string {
    let key = "";

    if (!filterNameParams) {
      filterNameParams = data.appliedFilters ? Object.keys(data.appliedFilters) : [];
    }

    filterNameParams.sort().forEach(filterName => {
      data.appliedFilters[filterName]?.forEach((item) => {
        key += item.toString();
      });
    })

    return key;
  }

  checkIfSameAppliedFilters(filters: any, cachedFiltersKey: string): boolean {
    const paramNames = ['budgets', 'benefit_category', 'tax_category', 'locations'];
    const filtersObj = {appliedFilters: {...filters}};
    const key = this.getKeyFromFilters(filtersObj, paramNames);
    return key === cachedFiltersKey;
  }

  getUserFixedBenefits(data: GetFixedBenefitsRequest): Observable<GetFixedBenefitsResponse> {
    if (this.fixedBenefitsCache && this.fixedBenefitsCache[data.companyId]) {
      const cache = this.fixedBenefitsCache[data.companyId];
      if (data.paginationCriteria.start === 0)
        return of(cache);
      else if (data.paginationCriteria.start + data.paginationCriteria.limit - 1 < cache.responseObject.data.length) {
        return of({
          ...cache,
          responseObject: {
            ...cache.responseObject,
            data: cache.responseObject.data.slice(data.paginationCriteria.start, data.paginationCriteria.start + data.paginationCriteria.limit)
          }
        });
      }
    }

    // If a request for this companyId is already in flight, reuse it instead of
    // firing a duplicate HTTP call, which would otherwise append the same data twice.
    if (this.fixedBenefitsPendingRequests[data.companyId]) {
      return this.fixedBenefitsPendingRequests[data.companyId];
    }

    const request = this._http.post<GetFixedBenefitsResponse>(EndpointService.getUserFixedBenefits, data)
      .pipe(tap({
        next: value => {
          const cache = this.fixedBenefitsCache[data.companyId] ?? {};

          this.fixedBenefitsCache[data.companyId] = {
            ...cache,
            responseObject: {
              ...cache.responseObject,
              ...value.responseObject,
              data: [...cache.responseObject?.data ?? [], ...value.responseObject.data]
            }
          };
        },
        error: () => {}
      }),
      finalize(() => {
        delete this.fixedBenefitsPendingRequests[data.companyId];
      }),
      shareReplay());

    this.fixedBenefitsPendingRequests[data.companyId] = request;

    return request;
  }

  getUserFixedBenefitDetails(benefitId: string) {
    return this._http.get<GetFixedBenefitResponse>(EndpointService.getUserFixedBenefitDetails + `?id=${benefitId}`);
  }

  getBenefitPhotos(data: GetPhotosRequest) {
    return this._http.post<GetPhotosResponse>(EndpointService.getBenefitPhotos, data);
  }

  getUserFlexibleBenefitDetails(benefitId: number) {
    return this._http.get<GetFlexibleBenefitResponse>(EndpointService.getUserFlexibleBenefitDetails + `/${benefitId}`);
  }

  getUserFixedBenefitDiscounts(){
    return this._http.get<any>(EndpointService.getUserFixedBenefitDiscounts);
  }

  addFixedBenefitExpenseReport(data: any){
    return this._http.post<any>(EndpointService.addFixedBenefitExpenseReport, data);
  }

  getProvidersCachedFilterKey() {
    return this.providersCardInfoCache.cachedFilter;
  }

  getProviderBenefitsCachedFilterKey(companyId: number, providerId: number) {
    let key = "";

    if (this.providersCardInfoCache[companyId] && this.providersCardInfoCache[companyId][providerId]) {
      key = this.providersCardInfoCache[companyId][providerId].cachedFilter;
    }

    return key;
  }

  clearCache() {
    this.providersCardInfoCache = {};
    this.offerBenefitsCache = {};
    this.flexibleBenefitsCache = {};
    this.fixedBenefitsCache = {};
    this.fixedBenefitsPendingRequests = {};
    this.flexibleBenefitsPendingRequests = {};
    this.providersCardInfoPendingRequest = null;
    this.offerBenefitsPendingRequests = {};
  }

  clearProvidersCardInfoCache() {
    this.providersCardInfoCache = {};
    this.providersCardInfoPendingRequest = null;
  }

  clearBenefitsCache(companyId: number, providerId: number) {
    if (this.flexibleBenefitsCache[companyId] && this.flexibleBenefitsCache[companyId][providerId]) {
      this.flexibleBenefitsCache[companyId][providerId] = {};
    }
    delete this.flexibleBenefitsPendingRequests[`${companyId}_${providerId}`];
  }
}
